import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalToken } from 'antd';

import { EPInputSearch, Auth } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { EPButton } from '@/components';

import UsersTable from './UsersTable';

export const UsersTab = observer(() => {
  const { t } = useTranslation();
  const { usersStore } = useStores();
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onChangeSearchInput = e => {
    const value = e.target.value.trim();
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      await usersStore.search(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Clear search term
    setSearchTerm('');

    // Reset search results by explicitly searching with empty string
    usersStore.search('');

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [usersStore]);

  return (
    <Container>
      <div className="title-container">
        <h1>{t('Manage Users')}</h1>
      </div>

      <div className="action-bar-container">
        <div className="left">
          <EPInputSearch
            style={{ width: 286 }}
            placeholder={t('Search')}
            value={searchTerm}
            onChange={onChangeSearchInput}
          />
        </div>
        <div className="right">
          <Auth permission={permissions.CREATE_STATION_USER}>
            <Link to={usersStore.isLoading ? undefined : 'new'}>
              <EPButton type="primary" showSkeleton={usersStore.isLoading}>
                {t('Create new user')}
              </EPButton>
            </Link>
          </Auth>
        </div>
      </div>

      <div className="users-table-container">
        <UsersTable />
      </div>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  height: 100%;
  overflow: auto;

  .title-container {
    h1 {
      font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
    }
  }

  .action-bar-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: ${({ theme: { token } }) => token.marginXXL}px;
  }

  .users-table-container {
    flex-grow: 1;
    margin-top: ${({ theme: { token } }) => token.marginXXL}px;
    overflow: auto;
  }
`;
